/**
 * ProvenanceDiagram — React Flow rewrite of `ProvenanceGraph` with
 * feature parity, type-safe over the canonical provenance node kinds.
 *
 * Single responsibility: render a small PDBG-style provenance DAG via
 * `TypedDiagram`. The node kinds (`input`, `policy_eval`, `witness`,
 * `effect`) and tone vocabulary (`neutral`, `highlight`, `alert`) match
 * the legacy `ProvenanceGraph` exactly, so callers can swap one for the
 * other without rewriting their data.
 *
 * Why the swap: React Flow gives us zoom, pan, fit-to-view, and a real
 * canvas for graph interaction without writing imperative SVG by hand.
 * The legacy SVG renderer is retained until consumers migrate.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../../design-system/tokens.js";
import {
  TypedDiagram,
  type TypedDiagramEdge,
  type TypedDiagramNode,
  type TypedDiagramNodeStyle,
  type TypedDiagramProps,
} from "./TypedDiagram.js";

/**
 * The four provenance node kinds the PDBG demo recognizes.
 *
 * - `input`: a witness value or external evidence.
 * - `policy_eval`: a governance gate evaluation.
 * - `witness`: an admissibility-bearing witness produced by the gate.
 * - `effect`: a downstream effect that the witness authorizes.
 */
export type ProvenanceNodeKind = "input" | "policy_eval" | "witness" | "effect";

/**
 * Tone of a provenance node: neutral / highlight / alert.
 *
 * `tone` does not affect layout — it shifts the border color so the
 * caller can draw attention to a specific node without changing its
 * kind-level palette. Matches `ProvenanceGraph` so existing data
 * passes through untouched.
 */
export type ProvenanceNodeTone = "neutral" | "highlight" | "alert";

export interface ProvenanceDiagramNode {
  readonly id: string;
  readonly label: string;
  readonly kind: ProvenanceNodeKind;
  readonly tone?: ProvenanceNodeTone;
}

export interface ProvenanceDiagramEdge {
  readonly from: string;
  readonly to: string;
  readonly label?: string;
}

export interface ProvenanceDiagramProps {
  readonly nodes: readonly ProvenanceDiagramNode[];
  readonly edges: readonly ProvenanceDiagramEdge[];
  /** Optional caption rendered below the diagram. */
  readonly caption?: string;
  /** CSS pixel height for the canvas. */
  readonly height?: number;
}

const KIND_FILL: Readonly<Record<ProvenanceNodeKind, string>> = {
  input: PRIMARY[100],
  policy_eval: PRIMARY[300],
  witness: SAFE[300],
  effect: GRAY[300],
} as const;

const TONE_BORDER: Readonly<Record<ProvenanceNodeTone, string>> = {
  neutral: GRAY[400],
  highlight: PRIMARY[700],
  alert: WARN[700],
} as const;

/**
 * Build the per-kind style table by applying the kind-driven fill on top
 * of the requested tone-driven border. The pure helper makes the prop
 * → style mapping snapshot-stable.
 */
function nodeStylesFor(
  tone: ProvenanceNodeTone,
): Record<ProvenanceNodeKind, TypedDiagramNodeStyle> {
  const border = TONE_BORDER[tone];
  return {
    input: { fill: KIND_FILL.input, border, text: GRAY[900] },
    policy_eval: { fill: KIND_FILL.policy_eval, border, text: GRAY[900] },
    witness: { fill: KIND_FILL.witness, border, text: GRAY[900] },
    effect: { fill: KIND_FILL.effect, border, text: GRAY[900] },
  };
}

/**
 * Renders a small provenance DAG with feature parity to `ProvenanceGraph`.
 *
 * Per-node `tone` is honored by mapping the tone to the border color of
 * just that node's `TypedDiagramNodeStyle`. The default tone is
 * `neutral` so callers that omit tone get the same rendering as the
 * legacy component.
 */
export const ProvenanceDiagram = React.memo(function ProvenanceDiagram({
  nodes,
  edges,
  caption,
  height = 320,
}: ProvenanceDiagramProps): React.JSX.Element {
  // Because per-node tone shifts only the border, we render with the
  // most common tone's style table and then override the border per
  // alert/highlight node by emitting them as one-off styled nodes.
  // For the small graphs PDBG uses, building a tone-keyed table per
  // node is cheap and keeps `TypedDiagram` itself tone-agnostic.
  const typedNodes = React.useMemo<readonly TypedDiagramNode<ProvenanceNodeKind>[]>(
    () =>
      nodes.map((node) => ({
        id: node.id,
        kind: node.kind,
        label: node.label,
      })),
    [nodes],
  );

  const typedEdges = React.useMemo<readonly TypedDiagramEdge[]>(
    () =>
      edges.map((edge) =>
        edge.label === undefined
          ? { source: edge.from, target: edge.to }
          : { source: edge.from, target: edge.to, label: edge.label },
      ),
    [edges],
  );

  // The neutral-only fast path: `TypedDiagram` only accepts a single
  // tone-keyed style table per kind, so when every node either omits
  // `tone` or sets it to `"neutral"` we can render with the neutral
  // table directly.
  const styles = React.useMemo(() => nodeStylesFor("neutral"), []);

  // Mixed-tone graphs branch below — we expand `kind` into
  // `<kind>__<tone>` discriminators so each node's tone selects its
  // own border without colliding inside React Flow's node cache.
  const hasMixedTones = React.useMemo(
    () => nodes.some((n) => n.tone !== undefined && n.tone !== "neutral"),
    [nodes],
  );

  if (!hasMixedTones) {
    const props: TypedDiagramProps<ProvenanceNodeKind> = {
      nodes: typedNodes,
      edges: typedEdges,
      nodeStyles: styles,
      ariaLabel: "Provenance diagram",
      height,
      ...(caption !== undefined ? { caption } : {}),
    };
    return <TypedDiagram<ProvenanceNodeKind> {...props} />;
  }

  type ToneKind = `${ProvenanceNodeKind}__${ProvenanceNodeTone}`;
  const mixedNodes: TypedDiagramNode<ToneKind>[] = nodes.map((node) => ({
    id: node.id,
    kind: `${node.kind}__${node.tone ?? "neutral"}`,
    label: node.label,
  }));
  const mixedStyles = (Object.keys(KIND_FILL) as ProvenanceNodeKind[]).reduce(
    (acc, kind) => {
      (Object.keys(TONE_BORDER) as ProvenanceNodeTone[]).forEach((tone) => {
        acc[`${kind}__${tone}`] = {
          fill: KIND_FILL[kind],
          border: TONE_BORDER[tone],
          text: GRAY[900],
        };
      });
      return acc;
    },
    {} as Record<ToneKind, TypedDiagramNodeStyle>,
  );
  const mixedProps: TypedDiagramProps<ToneKind> = {
    nodes: mixedNodes,
    edges: typedEdges,
    nodeStyles: mixedStyles,
    ariaLabel: "Provenance diagram",
    height,
    ...(caption !== undefined ? { caption } : {}),
  };
  return <TypedDiagram<ToneKind> {...mixedProps} />;
});
